import * as _ from 'lodash-es';
import React from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import generic from '../images/empty/generic.svg';
import googleDrive from '../images/empty/google-drive.svg';
import slack from '../images/empty/slack.svg';
import { SEARCH_PLATFORM_INFO_MAP } from '../integrations/IntegrationPlatformInfo';
import useIsLayoutSize from '../post/utils/useIsLayoutSize';
import { LayoutSize } from '../post/utils/useLayoutSize';
import * as styles from './EncourageInstallationPlatform.module.scss';
import ExternalBrandButton from './ExternalBrandButton';

const PLATFORM_FRAGMENT = gql(`
  fragment EncourageInstallationPlatform_platform on ExternalSearchPlatform {
    platform
    personalAuthUrl
    teamAuthUrl
  }
`);

interface EncourageInstallationPlatformProps {
  platform: DocumentType<typeof PLATFORM_FRAGMENT>;
}

const EMPTY_IMAGES = {
  'google-drive': googleDrive,
  slack,
  generic,
};

const EncourageInstallationPlatform = ({
  platform,
}: EncourageInstallationPlatformProps) => {
  const { platform: platformKey, personalAuthUrl, teamAuthUrl } = platform;
  const isMobile = useIsLayoutSize(LayoutSize.mobile);
  const isAdmin = useAppSelector(
    (state) => state.session.user.type === 'ADMIN',
  );
  const canConnectForTeam = isAdmin && !isMobile;

  const platformInfo = SEARCH_PLATFORM_INFO_MAP[platformKey];
  if (!platformInfo) return null;
  const { icon, name } = platformInfo;

  return (
    <div className={styles.container}>
      <div>
        <img
          alt={_.startCase(`${name} Preview`)}
          src={
            EMPTY_IMAGES[platformKey as keyof typeof EMPTY_IMAGES] ??
            EMPTY_IMAGES.generic
          }
        />
      </div>
      <div className={styles.content}>
        <h2>Search {name}</h2>
        <div className={styles.description}>
          {canConnectForTeam ? (
            <>
              Connect a {name} account to search from Slab. Enable for just
              yourself or your entire team.
            </>
          ) : (
            <>
              Connect a {name} account to search from Slab. Results are only
              visible to you.
            </>
          )}
        </div>
        {canConnectForTeam && (
          <span className={styles.buttonsHeader}>Connect {name} for:</span>
        )}
        <div className={styles.buttonsContainer}>
          {canConnectForTeam ? (
            <>
              {personalAuthUrl && (
                <ExternalBrandButton icon={icon} to={personalAuthUrl}>
                  Myself
                </ExternalBrandButton>
              )}
              {teamAuthUrl && (
                <ExternalBrandButton icon={icon} to={teamAuthUrl}>
                  My Team
                </ExternalBrandButton>
              )}
            </>
          ) : (
            personalAuthUrl && (
              <ExternalBrandButton icon={icon} to={personalAuthUrl}>
                Connect {name}
              </ExternalBrandButton>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default EncourageInstallationPlatform;
